import { useCallback } from 'react';
import { debounce } from '../utils/debounce';
import { TMDB_API_KEY, TMDB_READ_ACCESS_TOKEN } from '../constants';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchWithRetry = debounce(async (url, options, retries = 3, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      const response = await fetch(url, options);
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        await sleep(retryAfter ? parseInt(retryAfter) * 1000 : delay * Math.pow(2, i));
        continue;
      }
      if (!response.ok) {
        if (url.includes('/movie/') && response.status === 404) return { status: 404 };
        throw new Error(`HTTP ${response.status}`);
      }
      return response;
    } catch (e) {
      if (i === retries - 1) throw e;
      await sleep(delay * Math.pow(2, i));
    }
  }
});

export function useTMDB() {
  const getHeaders = useCallback(() => ({
    accept: 'application/json',
    Authorization: `Bearer ${TMDB_READ_ACCESS_TOKEN}`,
  }), []);

  const searchMovies = useCallback(async (query) => {
    if (!query) return [];
    const url = `https://api.themoviedb.org/3/search/movie?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(query)}&include_adult=false`;
    const res = await fetchWithRetry(url, { headers: getHeaders() });
    const data = await res.json();
    return data.results || [];
  }, [getHeaders]);

  const searchTVShows = useCallback(async (query) => {
    if (!query) return [];
    const url = `https://api.themoviedb.org/3/search/tv?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(query)}&include_adult=false`;
    const res = await fetchWithRetry(url, { headers: getHeaders() });
    const data = await res.json();
    return data.results || [];
  }, [getHeaders]);

  const searchPeople = useCallback(async (query) => {
    if (!query) return [];
    const url = `https://api.themoviedb.org/3/search/person?api_key=${TMDB_API_KEY}&query=${encodeURIComponent(query)}&include_adult=false`;
    const res = await fetchWithRetry(url, { headers: getHeaders() });
    const data = await res.json();
    return data.results || [];
  }, [getHeaders]);

  const getMovieDetails = useCallback(async (id) => {
    const url = `https://api.themoviedb.org/3/movie/${id}?api_key=${TMDB_API_KEY}&append_to_response=credits,external_ids`;
    const res = await fetchWithRetry(url, { headers: getHeaders() });
    if (res.status === 404) return null;
    const data = await res.json();
    return {
      runtime: data.runtime || null,
      release_date: data.release_date || null,
      directors: data.credits?.crew?.filter(p => p.job === 'Director').map(p => ({ name: p.name, id: p.id })) || [],
      writers: data.credits?.crew?.filter(p => p.department === 'Writing').map(p => ({ name: p.name, id: p.id })) || [],
      cast: data.credits?.cast?.slice(0, 6).map(p => ({ name: p.name, id: p.id })) || [],
      overview: data.overview || '',
      poster_path: data.poster_path || null,
      imdbId: data.external_ids?.imdb_id || null,
      title: data.title,
      tmdbId: data.id
    };
  }, [getHeaders]);

  const getTVDetails = useCallback(async (id) => {
    const url = `https://api.themoviedb.org/3/tv/${id}?api_key=${TMDB_API_KEY}&append_to_response=external_ids`;
    const res = await fetchWithRetry(url, { headers: getHeaders() });
    if (res.status === 404) return null;
    const data = await res.json();
    return {
      imdbId: data.external_ids?.imdb_id || null,
      seasons: data.seasons || [],
      last_episode_to_air: data.last_episode_to_air || null,
      name: data.name,
      overview: data.overview,
      poster_path: data.poster_path,
      tmdbId: data.id,
      first_air_date: data.first_air_date
    };
  }, [getHeaders]);

  const getCreatorFilmography = useCallback(async (pid) => {
    const res = await fetchWithRetry(`https://api.themoviedb.org/3/person/${pid}/movie_credits?api_key=${TMDB_API_KEY}`, { headers: getHeaders() });
    const d = await res.json();
    const map = new Map();
    [...(d.cast || []), ...(d.crew || [])].forEach(m => {
      if (!m || !m.id || !m.title || ['Thanks', 'Special Thanks', 'Executive Producer', 'Producer'].includes(m.job)) return;
      const isDir = m.department === 'Directing' && m.job === 'Director';
      const isWri = m.department === 'Writing';
      if (isDir || isWri) {
        if (!map.has(m.id)) {
          map.set(m.id, {
            tmdbId: m.id,
            title: m.title,
            year: m.release_date ? parseInt(m.release_date.substring(0, 4)) : null,
            release_date: m.release_date || null,
            seen: false,
            toWatch: false,
            notInterested: false,
            roles: [],
            directors: [],
            writers: [],
            cast: [],
            overview: '',
            poster_path: m.poster_path || null,
            runtime: null,
            imdbId: null
          });
        }
        const f = map.get(m.id);
        if (isDir && !f.roles.includes('Director')) f.roles.push('Director');
        if (isWri && !f.roles.includes('Writer')) f.roles.push('Writer');
        if (m.release_date && m.release_date.match(/^\d{4}-\d{2}-\d{2}$/) && (!f.release_date || m.release_date < f.release_date)) {
          f.release_date = m.release_date;
          f.year = parseInt(m.release_date.substring(0, 4));
        }
      }
    });

    const ids = Array.from(map.keys());
    const BATCH = 5;
    const DELAY = 200;
    
    // We need to use the getMovieDetails function defined above, but since it's a callback we can just call it if we bind it or use the same logic.
    // To avoid dependency issues inside the hook, we'll just implement a localized fetch.
    const fetchMovieDetail = async (id) => {
      const url = `https://api.themoviedb.org/3/movie/${id}?api_key=${TMDB_API_KEY}&append_to_response=credits,external_ids`;
      const res = await fetchWithRetry(url, { headers: getHeaders() });
      if (res.status === 404) return null;
      return await res.json();
    };

    for (let i = 0; i < ids.length; i += BATCH) {
      const batch = ids.slice(i, i + BATCH);
      const results = await Promise.all(batch.map(id => fetchMovieDetail(id).catch(() => null)));
      results.forEach((det, idx) => {
        if (det) {
          const f = map.get(batch[idx]);
          f.directors = det.credits?.crew?.filter(p => p.job === 'Director').map(p => ({ name: p.name, id: p.id })) || [];
          f.writers = det.credits?.crew?.filter(p => p.department === 'Writing').map(p => ({ name: p.name, id: p.id })) || [];
          f.cast = det.credits?.cast?.slice(0, 6).map(p => ({ name: p.name, id: p.id })) || [];
          f.runtime = det.runtime || null;
          f.imdbId = det.external_ids?.imdb_id || null;
          f.overview = det.overview || '';
          f.poster_path = det.poster_path || f.poster_path;
          if (det.release_date && det.release_date.match(/^\d{4}-\d{2}-\d{2}$/) && det.release_date !== f.release_date) {
            f.release_date = det.release_date;
            f.year = parseInt(det.release_date.substring(0, 4));
          }
        }
      });
      if (i + BATCH < ids.length) await sleep(DELAY);
    }
    return Array.from(map.values());
  }, [getHeaders]);

  return {
    searchMovies,
    searchTVShows,
    searchPeople,
    getMovieDetails,
    getTVDetails,
    getCreatorFilmography
  };
}
